import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";

// ── Wedding schedule ────────────────────────────────────────────────────────
const WEDDING_DATE = "2026-05-23";
const SCHEDULE = [
  { time: "15:30", label: "Ceremony" },
  { time: "16:00", label: "Photographs with Family & Friends" },
  { time: "16:30", label: "Drinks Reception & Canapés" },
  { time: "18:00", label: "Speeches & Toast" },
  { time: "18:30", label: "New Forest BBQ Feast" },
  { time: "19:30", label: "Cutting of the Cake" },
  { time: "20:00", label: "First Dance" },
  { time: "20:15", label: "Party Time Everyone" },
  { time: "00:00", label: "Reception Ends" },
];

// ── Quick-action chips shown across the bar ─────────────────────────────────
const QUICK_CHIPS = [
  { label: "📸 Upload photos", key: "upload" },
  { label: "🤳 Selfie tips", key: "selfie" },
  { label: "📖 Guestbook", key: "guestbook" },
  { label: "🪑 My seat", key: "seat" },
  { label: "⏰ Schedule", key: "schedule" },
  { label: "🖼️ Gallery", key: "gallery" },
  { label: "🎵 DJ Requests", key: "dj" },
];

// ── Bella response bank — witty AI, not doggo ──────────────────────────────
const NUDGE_MSGS = [
  (n) => `Hey ${n || "you"} 👋 Don't forget to upload a photo — Maggie & John want ALL the evidence from today. Yes, including the embarrassing ones. Especially those.`,
  (n) => `${n || "Psst"} — have you signed the guestbook yet? A voice note would make the happy couple cry actual tears. No pressure. Okay, some pressure.`,
  (n) => `${n || "Hey"}, the selfie page is sitting there unused and honestly it's feeling neglected. Tap Selfie at the top. You'll thank yourself later.`,
  (n) => `Quick reminder: Guest Photos has a Photo of the Day vote happening. Someone needs to win that completely made-up trophy. Might as well be you.`,
  (n) => `The gallery is filling up beautifully ${n ? n + "!" : "!"} You should go have a browse — there's a solid chance someone got a photo of you you didn't know about. Exciting and terrifying.`,
];

const GREETINGS = [
  (n) => `Hey ${n || "there"}! 👋 I'm Bella — the wedding app's slightly over-caffeinated AI assistant. I know this app inside and out, so fire away. Or just tap one of those buttons up top.`,
  (n) => `Oh hello ${n || "gorgeous"}! I'm Bella, your personal wedding app guide. Ask me anything — photos, the schedule, where to sit — I've got you covered. Probably.`,
];

const RESPONSES = {
  upload: [
    (n) => `Uploading is easy, ${n || "friend"}! Hit the **Upload** tab at the top, pick your best shots (or worst — we don't judge), and they'll appear in the Gallery live. Videos too. Go on.`,
    (n) => `The **Upload** tab is right up top, ${n || ""}. Tap it, choose your photos or videos, add a caption if you're feeling poetic. Maggie and John will see everything. Choose wisely. Or don't. We love chaos.`,
  ],
  selfie: [
    (n) => `Tap **Selfie** in the top navigation bar. There's a countdown timer so you can actually look ready, unlike every other photo taken of you today. Filters, props, the works. You're welcome.`,
    (n) => `Head to the **Selfie** tab at the top of the page${n ? ", " + n : ""}. There's a timer, flip camera, fun filters and props. It's basically a professional photoshoot but you're doing it yourself. Very on brand for 2026.`,
  ],
  guestbook: [
    (n) => `The **Guestbook** is where you leave Maggie and John something they'll actually keep. Text message, voice note, heartfelt poem — whatever you've got. Tap **Book** in the top navigation.`,
    (n) => `Tap **Book** in the top nav. You can write a message OR record a voice note. The voice notes genuinely make people cry at future anniversaries. Consider that your mission.`,
  ],
  gallery: [
    (n) => `**Gallery** is in the top navigation — every photo and video guests have shared, updating live. Filter by selfies, videos, favourites. If you spot yourself looking great, heart it immediately.`,
    (n) => `Tap **Gallery** at the top. It's all there — photos, videos, everything. Fair warning: someone may already have a photo of you dancing that you don't know about yet.`,
  ],
  seat: [
    (n) => `For your seat, go to the **Home page** and scroll down to the "Wedding Day Info" section. Tap **"Find Your Seat"** — the full seating plan is right there. Please sit at the right table — the catering team is watching.`,
    (n) => `Head to the **Home page** (tap the house icon or "Home" in the nav), scroll down to "Wedding Day Info", and tap **"Find Your Seat"**. The seating plan will pop up for you.`,
  ],
  schedule: [
    () => {
      const todayStr = new Date().toISOString().slice(0, 10);
      if (todayStr !== WEDDING_DATE) {
        const events = SCHEDULE.map(e => `${e.time} — ${e.label}`).join("\n");
        return `Here's the full schedule:\n\n${events}\n\nYou can also tap **"Order of Events"** on the Home page under "Wedding Day Info" to see this in the app.`;
      }
      const now = new Date();
      const toMins = (t) => { const [h, m] = t.split(":").map(Number); return h === 0 ? 1440 + h * 60 + m : h * 60 + m; };
      const nowMins = now.getHours() * 60 + now.getMinutes();
      const current = SCHEDULE.slice().reverse().find(e => toMins(e.time) <= nowMins);
      const next = SCHEDULE.find(e => toMins(e.time) > nowMins);
      const events = SCHEDULE.map(e => `${e.time} — ${e.label}`).join("\n");
      let msg = `Full schedule:\n\n${events}\n\n`;
      if (current) msg += `🎉 Right now: **${current.label}**. `;
      if (next) msg += `Up next: **${next.label}** at ${next.time}.`;
      return msg;
    },
  ],
  vote: [
    (n) => `Head to **Guest Photos** in the top nav and tap the ❤️ on any photo to vote. The one with the most votes gets "Photo of the Day" — a deeply prestigious honour that I completely made up. Go vote.`,
  ],
  dj: [
    (n) => `Want to hear your favorite song? Tap the **DJ** tab in the top navigation and submit a song request! The DJ will see all the requests and play the best ones. 🎵`,
    (n) => `Head to **DJ Requests** (the music note icon in the top nav) and request your favorite tracks. The more specific you are with the song title and artist, the better!`,
  ],
  bar: [
    (n) => `Ha — I am many things but a barman is not one of them. Your best bet is finding an actual human. While you're up though, upload a selfie with your drink. Future you will love it.`,
    (n) => `Tragically outside my skill set. But if you find it, document it. That's what the **Upload** tab is for.`,
  ],
  food: [
    (n) => `I can't point you to the buffet but I strongly suggest photographing your plate. Some of the best wedding album photos are food ones. It's a fact I just made up but it feels true.`,
  ],
  thanks: [
    (n) => `You're very welcome ${n || ""}! Now go enjoy the day — and maybe upload one more photo before you leave. For the memories. And because I asked nicely.`,
    (n) => `Anytime! That's literally what I'm here for. Well, that and looking good as a floating icon. Nailed it on both counts.`,
  ],
  bye: [
    (n) => `Bye ${n || ""}! 👋 It's been a genuine pleasure. Go dance, eat cake, make memories — and upload a photo before you go. You know the drill.`,
  ],
  joke: [
    (n) => `Why did the wedding photographer get kicked out of the reception? He kept trying to "frame" the groom. I'll be here all evening — unlike the bar tab.`,
    (n) => `What do you call two spiders who just got married? Newly-webs. You're welcome. Now go upload a photo.`,
  ],
  whoami: [
    (n) => `I'm Bella — the wedding app's AI assistant. I know everything about this app and a surprising amount about weddings. Ask me about photos, the schedule, the guestbook, or anything else. I won't judge. Much.`,
  ],
  help: [
    (n) => `Here's what I can help with:\n\n📸 **Upload** tab — share photos & videos\n🖼️ **Gallery** tab — browse everyone's memories\n🤳 **Selfie** tab — snap a great photo\n📖 **Book** tab — leave a message or voice note\n👥 **Guests** tab — browse guests & vote\n🎵 **DJ** tab — request your favorite songs\n🏠 **Home page** → "Wedding Day Info" — seating plan, schedule, couple's story & journey\n\nOr just tap one of the quick buttons at the top of this chat.`,
  ],
  story: [
    (n) => `Maggie and John — what a pair! 💛 Go to the **Home page** and tap **"Maggie & John"** under "Wedding Day Info" to read their love story — it all started on a night out. Tap **"Our Journey"** too for a full photo slideshow.`,
  ],
  countdown: [
    () => {
      const now = new Date();
      const midnight = new Date(now); midnight.setHours(23, 59, 59, 999);
      const diff = midnight - now;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      return `About ${h} hours and ${m} minutes until midnight — savour every second. This day goes faster than you think. Dance more. Worry less.`;
    },
  ],
  navigate: [
    (n) => `Here's the full map of the app:\n\n🏠 **Home** — welcome, photo collage, seating plan, schedule, couple's story, DJ requests\n🖼️ **Gallery** — all guest photos & videos\n🤳 **Selfie** — take a fun selfie with filters & props\n📸 **Upload** — share your own photos & videos\n📖 **Book** — guestbook messages & voice notes\n👥 **Guests** — guest photos & Photo of the Day voting\n🎵 **DJ** — request your favorite songs for the DJ\n🏨 **Venue** — info about Bartley Lodge Hotel\n\nAll tabs are in the navigation bar at the top of every page.`,
  ],
  venue: [
    (n) => `You're celebrating at **Bartley Lodge Hotel** in Cadnam, New Forest, Hampshire 🌳\n\nIt's a beautiful Victorian country house hotel set in 3 acres of gardens. The address is **Cadnam, Southampton, SO40 2NR**.\n\nFor directions, maps, parking, and more details about the hotel, tap the **Venue** tab (the building icon) in the navigation bar.`,
    (n) => `The venue is **Bartley Lodge Hotel** — a gorgeous Victorian manor in the heart of the New Forest 🌿\n\nAddress: Cadnam, Southampton, SO40 2NR. There's parking on site.\n\nTap **Venue** in the top navigation for photos and full details.`,
  ],
};

const FALLBACK = [
  (n) => `Hmm, not sure I know that one ${n || ""}! Try asking about photos, the selfie page, guestbook, gallery or the seating plan. Or just tap one of the chips above. They're very tappable.`,
  (n) => `That one's outside my remit ${n || ""}! I'm a wedding app guide not a search engine — but I'm excellent at what I do. Try "help" for the full list.`,
  (n) => `You've stumped me! Ask about photos, selfies, the guestbook, the schedule or the seating plan. Or tap a quick button — those always work.`,
];

function matchResponse(input, name) {
  const t = input.toLowerCase();
  const pick = (arr) => {
    const fn = arr[Math.floor(Math.random() * arr.length)];
    return fn(name);
  };
  if (/upload|photo|picture|pic|share|video/.test(t)) return pick(RESPONSES.upload);
  if (/selfie|camera|snap|filter/.test(t)) return pick(RESPONSES.selfie);
  if (/guestbook|guest book|message|voice|note/.test(t)) return pick(RESPONSES.guestbook);
  if (/gallery|browse|view|see photo/.test(t)) return pick(RESPONSES.gallery);
  if (/seat|table|sit|where.*i|plan/.test(t)) return pick(RESPONSES.seat);
  if (/schedule|agenda|what.*happen|order|event|time/.test(t)) return RESPONSES.schedule[0]();
  if (/vote|heart|best photo|photo of the day/.test(t)) return pick(RESPONSES.vote);
  if (/dj|song|music|request|play/.test(t)) return pick(RESPONSES.dj);
  if (/bar|drink|alcohol|wine|beer|prosecco|champagne/.test(t)) return pick(RESPONSES.bar);
  if (/food|eat|buffet|hungry|menu/.test(t)) return pick(RESPONSES.food);
  if (/thank|thanks|cheers|brilliant|great/.test(t)) return pick(RESPONSES.thanks);
  if (/bye|goodbye|leaving|go now/.test(t)) return pick(RESPONSES.bye);
  if (/joke|funny|laugh|humour/.test(t)) return pick(RESPONSES.joke);
  if (/who are you|what are you|about you/.test(t)) return pick(RESPONSES.whoami);
  if (/help|what can|guide|how do/.test(t)) return pick(RESPONSES.help);
  if (/story|how.*meet|love story|couple/.test(t)) return pick(RESPONSES.story);
  if (/midnight|time left|countdown|how long/.test(t)) return RESPONSES.countdown[0]();
  if (/navigate|where is|find|map|pages|sections/.test(t)) return pick(RESPONSES.navigate);
  if (/venue|hotel|bartley|where.*wedding|where.*held|address|location|directions|parking/.test(t)) return pick(RESPONSES.venue);
  return pick(FALLBACK);
}

// ── Component ───────────────────────────────────────────────────────────────
export default function BellaChatbot() {
  const { name, role } = useAuth();
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [hidden] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom
  useEffect(() => {
    if (hidden) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, hidden]);

  // Greeting on open
  useEffect(() => {
    if (hidden) return;
    if (open && messages.length === 0) {
      const greet = GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
      const text = greet(name);
      setMessages([{ from: "bot", text, ts: Date.now() }]);
    }
  }, [open, messages.length, name, hidden]);

  // Nudge pulse
  useEffect(() => {
    if (hidden || open) return;
    const iv = setInterval(() => { setPulse(true); setTimeout(() => setPulse(false), 600); }, 45000);
    return () => clearInterval(iv);
  }, [hidden, open]);

  // Timed nudge messages
  useEffect(() => {
    if (hidden || open) return;
    const timer = setTimeout(() => {
      const fn = NUDGE_MSGS[Math.floor(Math.random() * NUDGE_MSGS.length)];
      const text = fn(name);
      setMessages(prev => [...prev, { from: "bot", text, ts: Date.now() }]);
      setPulse(true);
      setTimeout(() => setPulse(false), 600);
    }, 120000);
    return () => clearTimeout(timer);
  }, [hidden, open, name]);

  if (hidden) return null;

  const sendMessage = (text) => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, { from: "user", text, ts: Date.now() }]);
    setInput("");
    setTimeout(() => {
      const reply = matchResponse(text, name);
      setMessages(prev => [...prev, { from: "bot", text: reply, ts: Date.now() }]);
    }, 420);
  };

  const handleSend = () => sendMessage(input);
  const handleKeyDown = (e) => { if (e.key === "Enter") handleSend(); };

  const handleChip = (key) => {
    const chipLabels = {
      upload: "How do I upload photos?",
      selfie: "Selfie tips",
      guestbook: "How do I sign the guestbook?",
      seat: "Where is my seat?",
      schedule: "What's the schedule?",
      gallery: "How do I view the gallery?",
      dj: "How do I request a song?",
    };
    sendMessage(chipLabels[key] || key);
  };

  return (
    <>
      {/* Bella FAB */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-20 right-5 z-50 flex flex-col items-center gap-0.5 transition-all duration-300 ${pulse ? "scale-125" : "scale-100"}`}
        title="Chat with Bella"
      >
        <div
          className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #000 0%, #1a1a1a 100%)", border: "2px solid #D4AF37" }}
        >
          <img
            src="/bella-icon.png"
            alt="Bella"
            className="w-10 h-10 rounded-full object-cover"
            style={{ animation: open ? "none" : "bella-bob 2s ease-in-out infinite" }}
          />
        </div>
        <span
          className="text-[10px] text-white px-2 py-0.5 rounded-full shadow-md"
          style={{
            background: "linear-gradient(135deg, #000 0%, #1a1a1a 100%)",
            border: "1px solid #D4AF37",
            fontFamily: "'Playfair Display', serif",
            letterSpacing: "0.12em",
          }}
        >
          Bella
        </span>
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-36 right-4 z-50 w-80 sm:w-96 rounded-3xl shadow-2xl border border-[#E5E0D8] overflow-hidden flex flex-col"
          style={{ background: "#FDFBF7", maxHeight: "min(520px, calc(100dvh - 160px))" }}
        >
          {/* Header */}
          <div
            className="px-5 py-3.5 text-white flex items-center justify-between"
            style={{ background: "linear-gradient(135deg, #000 0%, #1a1a1a 100%)" }}
          >
            <div className="flex items-center gap-2.5">
              <img src="/bella-icon.png" alt="Bella" className="w-8 h-8 rounded-full border border-[#D4AF37]/50" />
              <div>
                <h3 className="font-bold text-sm tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>Bella ✨</h3>
                <p className="text-[10px] opacity-75" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Wedding AI · Always helpful, usually funny</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition text-lg leading-none">✕</button>
          </div>

          {/* Quick chips — scrollable horizontal bar, sticky below header */}
          <div
            className="flex gap-2 px-3 py-2 overflow-x-auto border-b border-[#E5E0D8] flex-shrink-0"
            style={{ background: "#F5F0E8", scrollbarWidth: "none" }}
          >
            {QUICK_CHIPS.map((chip) => (
              <button
                key={chip.key}
                onClick={() => handleChip(chip.key)}
                className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#FAF5E6] transition-all whitespace-nowrap"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: "#6B5F3E",
                  background: "white",
                }}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "#FDFBF7" }}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.from === "bot" ? "justify-start" : "justify-end"}`}>
                <div
                  className={`max-w-xs px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.from === "bot" ? "bg-[#E5E0D8] text-[#1A1A1A]" : "bg-[#8FAE7B] text-white"
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "0.9rem" }}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-[#E5E0D8] p-3 bg-white flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Bella anything…"
              className="flex-1 rounded-full border border-[#E5E0D8] px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            />
            <button
              onClick={handleSend}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ background: "linear-gradient(135deg, #8FAE7B 0%, #527946 100%)" }}
            >
              <span className="text-white text-lg">→</span>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bella-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
      `}</style>
    </>
  );
}
